/**
 * Pinia store for Neighbourhood feature (Vue 3 Composition API)
 *
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { aggregateNeighbourhoodData } from '@/helpers/Neighbourhood/neighbourhoodAggregator';
import { DEFAULT_PERSONA } from '@/helpers/Neighbourhood/neighbourhoodConstants';
import { AggregatedNeighbourhoodData } from '@/helpers/Neighbourhood/neighbourhoodDescriptionTypes';
import { NeighbourhoodPoiDescriptionResponse } from '@/helpers/Neighbourhood/neighbourhoodPOITypes';
import { PersonaType } from '@/helpers/Neighbourhood/personaTypes';
import { TravelTimeData } from '@/store/types/travel-time-state';
import { ComposerTranslation } from 'vue-i18n';

let abortController: AbortController | null = null;

function cancelAndCreateNewRequest(): AbortSignal {
  if (abortController) {
    abortController.abort();
  }

  abortController = new AbortController();
  return abortController.signal;
}

function handleFetchError(err: unknown, errorRef: { value: string | null }): void {
  if (err instanceof Error && err.name === 'AbortError') {
    return;
  }

  errorRef.value = err instanceof Error ? err.message : 'Failed to fetch neighbourhood data';
}

export const useNeighbourhoodStore = defineStore('neighbourhood', () => {
  const data = ref<AggregatedNeighbourhoodData | NeighbourhoodPoiDescriptionResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastFetchedCoordinates = ref<{ lat: number; lng: number } | null>(null);
  const lastFetchedPersona = ref<string | null>(null);
  const lastFetchedLocale = ref<string | null>(null);
  const lastRefinementAnswers = ref<Record<string, string> | null>(null);
  const poiCache = ref<Record<string, Record<string, TravelTimeData>>>({});
  const selectedPersona = ref<PersonaType>(PersonaType.Family);

  const neighbourhoodData = computed(() => data.value);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => !!error.value);

  function shouldUseCachedData(
    latitude: number,
    longitude: number,
    persona: string,
    locale: string,
    refinementAnswers?: Record<string, string>,
  ): boolean {
    const isSameLocation =
      lastFetchedCoordinates.value &&
      lastFetchedCoordinates.value.lat === latitude &&
      lastFetchedCoordinates.value.lng === longitude;

    const isSamePersona = lastFetchedPersona.value === persona;
    const isSameLocale = lastFetchedLocale.value === locale;

    const hasRefinementAnswers = refinementAnswers && Object.keys(refinementAnswers).length > 0;
    if (hasRefinementAnswers) {
      return false;
    }

    return !!(isSameLocation && isSamePersona && isSameLocale && data.value);
  }

  function handleSuccessfulFetch(
    neighbourhoodData: AggregatedNeighbourhoodData | NeighbourhoodPoiDescriptionResponse | null,
    latitude: number,
    longitude: number,
    normalizedPersona: string,
    normalizedLocale: string,
    refinementAnswers?: Record<string, string>,
  ): AggregatedNeighbourhoodData | NeighbourhoodPoiDescriptionResponse | null {
    if (!neighbourhoodData) {
      error.value = 'No neighbourhood data available';
      return null;
    }

    data.value = neighbourhoodData;
    lastFetchedCoordinates.value = { lat: latitude, lng: longitude };
    lastFetchedPersona.value = normalizedPersona;
    lastFetchedLocale.value = normalizedLocale;

    if (refinementAnswers) {
      lastRefinementAnswers.value = refinementAnswers;
    }

    return neighbourhoodData;
  }

  async function fetchNeighbourhoodData(payload: {
    latitude: number;
    longitude: number;
    persona?: string;
    locale?: string;
    refinementAnswers?: Record<string, string>;
    listingId: string;
    enableWebSearch?: boolean;
    municipalityName?: string;
    propertyType?: string;
    zipCode?: string;
    store?: any; // KK-2200: Kept for API compatibility but no longer used
    t: ComposerTranslation;
  }): Promise<AggregatedNeighbourhoodData | NeighbourhoodPoiDescriptionResponse | null> {
    const { latitude, longitude, persona, locale, refinementAnswers } = payload;
    const normalizedPersona = persona || DEFAULT_PERSONA;
    const normalizedLocale = locale || 'de';

    if (
      shouldUseCachedData(
        latitude,
        longitude,
        normalizedPersona,
        normalizedLocale,
        refinementAnswers,
      )
    ) {
      return data.value;
    }

    const signal = cancelAndCreateNewRequest();

    loading.value = true;
    error.value = null;

    try {
      const neighbourhoodData = await aggregateNeighbourhoodData(
        { ...payload, poiCacheStore: { getPOIData, setPOICache } },
        signal,
      );
      return handleSuccessfulFetch(
        neighbourhoodData,
        latitude,
        longitude,
        normalizedPersona,
        normalizedLocale,
        refinementAnswers,
      );
    } catch (err) {
      handleFetchError(err, error);
      return null;
    } finally {
      loading.value = false;
    }
  }

  function setSelectedPersona(persona: PersonaType) {
    selectedPersona.value = persona;
  }

  function clearNeighbourhoodData() {
    data.value = null;
    error.value = null;
    lastFetchedCoordinates.value = null;
    lastFetchedPersona.value = null;
    lastFetchedLocale.value = null;
    lastRefinementAnswers.value = null;
  }

  function getPOIData(listingId: string, persona: string): TravelTimeData | null {
    return poiCache.value[listingId]?.[persona] || null;
  }

  function setPOICache(listingId: string, persona: string, poiData: TravelTimeData): void {
    if (!poiCache.value[listingId]) {
      poiCache.value[listingId] = {};
    }
    poiCache.value[listingId][persona] = poiData;
  }

  return {
    data,
    loading,
    error,
    lastFetchedCoordinates,
    lastFetchedPersona,
    lastFetchedLocale,
    lastRefinementAnswers,
    poiCache,
    selectedPersona,
    neighbourhoodData,
    isLoading,
    hasError,
    fetchNeighbourhoodData,
    clearNeighbourhoodData,
    setSelectedPersona,
    getPOIData,
    setPOICache,
  };
});
